import * as React from 'react';
import { cn } from '@/lib/utils';

export interface TagListProps {
  tags: string[];
  maxVisible?: number;
  className?: string;
  onTagClick?: (tag: string) => void;
}

export const TagList = React.forwardRef<HTMLDivElement, TagListProps>(
  ({ tags, maxVisible = 5, className, onTagClick }, ref) => {
    const visibleTags = tags.slice(0, maxVisible);
    const remainingCount = tags.length - maxVisible;

    if (tags.length === 0) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn('flex flex-wrap items-center gap-1', className)}
        role="list"
        aria-label="Tags"
      >
        {visibleTags.map((tag) => (
          <span
            key={tag}
            role="listitem"
            className={cn(
              'inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300',
              onTagClick && 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700'
            )}
            onClick={() => onTagClick?.(tag)}
            onKeyDown={(e) => {
              if (onTagClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onTagClick(tag);
              }
            }}
            tabIndex={onTagClick ? 0 : undefined}
          >
            {tag}
          </span>
        ))}
        {remainingCount > 0 && (
          <span
            role="listitem"
            className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400"
          >
            +{remainingCount} more
          </span>
        )}
      </div>
    );
  }
);

TagList.displayName = 'TagList';
